// =============================================
// PAGINA CRIAR OBRA
// =============================================
// Arquivo: ObraCreate.tsx
// Descrição: Página de criação de nova obra
// =============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl, getDefaultHeaders } from '../../../services/config';
import FormLayout from '../../../components/Common/FormLayout';

// =============================================
// INTERFACES
// =============================================

interface ObraData {
  numero_contrato: string;
  numero_obra: string;
  cliente_nome: string;
  cliente_cpf_cnpj: string;
  cliente_endereco: string;
  cliente_telefone: string;
  cliente_email: string;
  titulo: string;
  descricao: string;
  categoria: string;
  endereco: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  latitude: number | null;
  longitude: number | null;
  valor_estimado: number;
  valor_aditivos: number;
  progresso_percentual: number;
  status: string;
  data_inicio_prevista: string;
  data_conclusao_prevista: string;
  responsavel_nome: string;
  responsavel_telefone: string;
  responsavel_email: string;
  observacoes: string;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

/**
 * Componente ObraCreate - Criação de nova obra
 * @returns JSX.Element - Componente de criação
 */
const ObraCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [obraData, setObraData] = useState<ObraData>({
    numero_contrato: '',
    numero_obra: '',
    cliente_nome: '',
    cliente_cpf_cnpj: '',
    cliente_endereco: '',
    cliente_telefone: '',
    cliente_email: '',
    titulo: '',
    descricao: '',
    categoria: 'residencial',
    endereco: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    latitude: null,
    longitude: null,
    valor_estimado: 0,
    valor_aditivos: 0,
    progresso_percentual: 0,
    status: 'planejada',
    data_inicio_prevista: '',
    data_conclusao_prevista: '',
    responsavel_nome: '',
    responsavel_telefone: '',
    responsavel_email: '',
    observacoes: ''
  });

  // =============================================
  // FUNÇÕES UTILITÁRIAS
  // =============================================

  /**
   * Trata mudança de valores
   */
  const handleInputChange = (field: keyof ObraData, value: string | number) => {
    setObraData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Formata valor monetário
   */
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  /**
   * Converte string monetária para number
   */
  const parseCurrency = (value: string) => {
    const cleanValue = value.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  /**
   * Trata mudança de valores monetários
   */
  const handleCurrencyChange = (field: keyof ObraData, value: string) => {
    const numericValue = parseCurrency(value);
    setObraData(prev => ({
      ...prev,
      [field]: numericValue
    }));
  };

  /**
   * Validação dos dados
   */
  const validateForm = () => {
    if (!obraData.numero_contrato.trim()) {
      setError('Número do contrato é obrigatório');
      return false;
    }

    if (!obraData.numero_obra.trim()) {
      setError('Número da obra é obrigatório');
      return false;
    }

    if (!obraData.titulo.trim()) {
      setError('Título da obra é obrigatório');
      return false;
    }

    if (!obraData.cliente_nome.trim()) {
      setError('Nome do cliente é obrigatório');
      return false;
    }

    if (!obraData.endereco.trim()) {
      setError('Endereço da obra é obrigatório');
      return false;
    }

    if (!obraData.cidade.trim()) {
      setError('Cidade é obrigatória');
      return false;
    }

    if (!obraData.estado.trim()) {
      setError('Estado é obrigatório');
      return false;
    }

    if (obraData.valor_estimado <= 0) {
      setError('Valor estimado deve ser maior que zero');
      return false;
    }

    if (!obraData.data_inicio_prevista) {
      setError('Data de início prevista é obrigatória');
      return false;
    }

    if (!obraData.data_conclusao_prevista) {
      setError('Data de conclusão prevista é obrigatória');
      return false;
    }

    setError(null);
    return true;
  };

  /**
   * Salva nova obra
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Obter headers padrão (inclui token automaticamente)
      const headers = getDefaultHeaders();

      console.log('Headers:', headers);
      console.log('Token presente:', headers.Authorization ? 'SIM' : 'NÃO');

      // Mapear campos do frontend para colunas exatas do banco de dados
      const submitData = {
        // Campos obrigatórios
        numero_obra: obraData.numero_obra,
        nome: obraData.titulo, // Frontend usa 'titulo', BD usa 'nome'
        categoria: obraData.categoria,

        // Descrição
        descricao: obraData.descricao || undefined,

        // Cliente
        cliente_nome: obraData.cliente_nome || undefined,
        cliente_cnpj: obraData.cliente_cpf_cnpj || undefined,
        cliente_contato: obraData.cliente_telefone || undefined,

        // Responsável Técnico
        responsavel_tecnico_nome: obraData.responsavel_nome || undefined,
        responsavel_tecnico_contato: obraData.responsavel_telefone || undefined,

        // Endereço
        endereco_logradouro: obraData.endereco || undefined,
        endereco_bairro: obraData.bairro || undefined,
        endereco_cidade: obraData.cidade || undefined,
        endereco_estado: obraData.estado || undefined,
        endereco_cep: obraData.cep || undefined,

        // Datas
        data_inicio_prevista: obraData.data_inicio_prevista || undefined,
        data_fim_prevista: obraData.data_conclusao_prevista || undefined, // Frontend usa 'data_conclusao_prevista', BD usa 'data_fim_prevista'

        // Valores
        valor_total: obraData.valor_estimado || undefined, // Frontend usa 'valor_estimado', BD usa 'valor_total'
        moeda: 'BRL',

        // Status e progresso
        status: obraData.status || 'planejamento',
        percentual_conclusao: obraData.progresso_percentual || 0, // Frontend usa 'progresso_percentual', BD usa 'percentual_conclusao'

        // Observações
        observacoes: obraData.observacoes || undefined
      };

      console.log('Enviando dados:', submitData);

      const response = await fetch(buildApiUrl('/obras'), {
        method: 'POST',
        headers,
        body: JSON.stringify(submitData)
      });

      console.log('Status da resposta:', response.status);

      if (response.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (response.status === 403) {
        setError('Você não tem permissão para criar obras.');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erro da API:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Obra criada com sucesso:', data);
      navigate(`/obras/visualizar/${data.id}`);
    } catch (err: any) {
      console.error('Erro ao criar obra:', err);
      setError(err.message || 'Erro ao salvar a obra. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  const handleCancel = () => {
    navigate('/obras');
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  return (
    <FormLayout
      title="Nova Obra"
      subtitle="Cadastro de Obra"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Cadastrar Obra"
      isLoading={loading}
      showActions={false}
    >
      {/* Formulário */}
      <form onSubmit={handleSubmit} className="obra-form">
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle"></i> {error}
          </div>
        )}

        {/* Informações Básicas */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Informações Básicas
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="numero_contrato">Número do Contrato *</label>
              <input
                type="text"
                id="numero_contrato"
                value={obraData.numero_contrato}
                onChange={(e) => handleInputChange('numero_contrato', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="numero_obra">Número da Obra *</label>
              <input
                type="text"
                id="numero_obra"
                value={obraData.numero_obra}
                onChange={(e) => handleInputChange('numero_obra', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="titulo">Título da Obra *</label>
              <input
                type="text"
                id="titulo"
                value={obraData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoria">Categoria *</label>
              <select
                id="categoria"
                value={obraData.categoria}
                onChange={(e) => handleInputChange('categoria', e.target.value)}
                required
              >
                <option value="residencial">Residencial</option>
                <option value="comercial">Comercial</option>
                <option value="industrial">Industrial</option>
                <option value="infraestrutura">Infraestrutura</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="descricao">Descrição</label>
            <textarea
              id="descricao"
              value={obraData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Cliente */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Cliente
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="cliente_nome">Nome do Cliente *</label>
              <input
                type="text"
                id="cliente_nome"
                value={obraData.cliente_nome}
                onChange={(e) => handleInputChange('cliente_nome', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="cliente_cpf_cnpj">CPF/CNPJ</label>
              <input
                type="text"
                id="cliente_cpf_cnpj"
                value={obraData.cliente_cpf_cnpj}
                onChange={(e) => handleInputChange('cliente_cpf_cnpj', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cliente_telefone">Telefone</label>
              <input
                type="tel"
                id="cliente_telefone"
                value={obraData.cliente_telefone}
                onChange={(e) => handleInputChange('cliente_telefone', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cliente_email">E-mail</label>
              <input
                type="email"
                id="cliente_email"
                value={obraData.cliente_email}
                onChange={(e) => handleInputChange('cliente_email', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="cliente_endereco">Endereço do Cliente</label>
            <input
              type="text"
              id="cliente_endereco"
              value={obraData.cliente_endereco}
              onChange={(e) => handleInputChange('cliente_endereco', e.target.value)}
            />
          </div>
        </div>

        {/* Localização */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Localização da Obra
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="endereco">Endereço *</label>
              <input
                type="text"
                id="endereco"
                value={obraData.endereco}
                onChange={(e) => handleInputChange('endereco', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="bairro">Bairro</label>
              <input
                type="text"
                id="bairro"
                value={obraData.bairro}
                onChange={(e) => handleInputChange('bairro', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cidade">Cidade *</label>
              <input
                type="text"
                id="cidade"
                value={obraData.cidade}
                onChange={(e) => handleInputChange('cidade', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="estado">Estado *</label>
              <input
                type="text"
                id="estado"
                value={obraData.estado}
                onChange={(e) => handleInputChange('estado', e.target.value)}
                maxLength={2}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="cep">CEP</label>
              <input
                type="text"
                id="cep"
                value={obraData.cep}
                onChange={(e) => handleInputChange('cep', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Valores Financeiros */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Valores Financeiros
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="valor_estimado">Valor Estimado (R$) *</label>
              <input
                type="text"
                id="valor_estimado"
                value={formatCurrency(obraData.valor_estimado)}
                onChange={(e) => handleCurrencyChange('valor_estimado', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="valor_aditivos">Valor de Aditivos (R$)</label>
              <input
                type="text"
                id="valor_aditivos"
                value={formatCurrency(obraData.valor_aditivos)}
                onChange={(e) => handleCurrencyChange('valor_aditivos', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                value={obraData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                required
              >
                <option value="planejada">Planejada</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="paralisada">Paralisada</option>
                <option value="finalizada">Finalizada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cronograma */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Cronograma
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="data_inicio_prevista">Data Início Prevista *</label>
              <input
                type="date"
                id="data_inicio_prevista"
                value={obraData.data_inicio_prevista}
                onChange={(e) => handleInputChange('data_inicio_prevista', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="data_conclusao_prevista">Data Conclusão Prevista *</label>
              <input
                type="date"
                id="data_conclusao_prevista"
                value={obraData.data_conclusao_prevista}
                onChange={(e) => handleInputChange('data_conclusao_prevista', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="progresso_percentual">Progresso (%)</label>
              <input
                type="number"
                id="progresso_percentual"
                min="0"
                max="100"
                value={obraData.progresso_percentual}
                onChange={(e) => handleInputChange('progresso_percentual', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        </div>

        {/* Responsável */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Responsável pela Obra
          </h3>
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="form-group">
              <label htmlFor="responsavel_nome">Nome</label>
              <input
                type="text"
                id="responsavel_nome"
                value={obraData.responsavel_nome}
                onChange={(e) => handleInputChange('responsavel_nome', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="responsavel_telefone">Telefone</label>
              <input
                type="tel"
                id="responsavel_telefone"
                value={obraData.responsavel_telefone}
                onChange={(e) => handleInputChange('responsavel_telefone', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="responsavel_email">E-mail</label>
              <input
                type="email"
                id="responsavel_email"
                value={obraData.responsavel_email}
                onChange={(e) => handleInputChange('responsavel_email', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Observações */}
        <div className="form-section" style={{ marginBottom: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
            Observações
          </h3>
          <div className="form-group">
            <label htmlFor="observacoes">Observações Adicionais</label>
            <textarea
              id="observacoes"
              value={obraData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
              rows={4}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Salvando...' : <><i className="fas fa-save"></i> Salvar Obra</>}
          </button>

          <button type="button" onClick={handleCancel} className="btn-secondary">
            <i className="fas fa-times"></i> Cancelar
          </button>
        </div>
      </form>
    </FormLayout>
  );
};

export default ObraCreate;
